import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useOrg } from '../OrgProvider';
import { canManageOrg } from '../org-roles';
import { fetchOrgStorage, updateOrgStorage, deleteOrgStorage } from '../api';
import type { OrgStorageConfig } from '../types';

/**
 * Point an organization's payloads at its own S3-compatible bucket.
 *
 * Only the payloads move: `page_index.json` and `chunks.jsonl`, which are
 * verbatim derivatives of the org's source. Metadata and vectors stay in the
 * shared database, which is what lets one query rank an org's private packages
 * against the public catalog.
 *
 * The secret access key is write-only — a read returns the bucket, region, and
 * access key id, never the secret.
 */
export function OrgStoragePanel() {
  const { getToken } = useAuth();
  const { activeOrg } = useOrg();
  const canManage = canManageOrg(activeOrg);

  const [config, setConfig] = useState<OrgStorageConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const [bucket, setBucket] = useState('');
  const [region, setRegion] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [forcePathStyle, setForcePathStyle] = useState(false);

  const applyConfig = useCallback((next: OrgStorageConfig) => {
    setConfig(next);
    setBucket(next.bucket ?? '');
    setRegion(next.region ?? '');
    setEndpoint(next.endpoint ?? '');
    setAccessKeyId(next.accessKeyId ?? '');
    setForcePathStyle(next.forcePathStyle);
    setSecretKey('');
  }, []);

  const load = useCallback(async () => {
    if (!activeOrg) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      applyConfig(await fetchOrgStorage(activeOrg.id, token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load storage config.');
    } finally {
      setLoading(false);
    }
  }, [activeOrg, getToken, applyConfig]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!activeOrg) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      applyConfig(
        await updateOrgStorage(
          activeOrg.id,
          {
            bucket: bucket.trim(),
            region: region.trim(),
            endpoint: endpoint.trim() || null,
            accessKeyId: accessKeyId.trim(),
            // Omitted when blank so editing the bucket doesn't clear the credential.
            secretAccessKey: secretKey.trim() || undefined,
            forcePathStyle,
          },
          token
        )
      );
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save storage config.');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!activeOrg) return;
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;
      await deleteOrgStorage(activeOrg.id, token);
      setConfirmingReset(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset storage config.');
    } finally {
      setSaving(false);
    }
  }

  if (!activeOrg) return null;

  const canSave =
    bucket.trim() !== '' &&
    region.trim() !== '' &&
    accessKeyId.trim() !== '' &&
    (config?.configured || secretKey.trim() !== '');

  return (
    <div className="space-y-4 border border-rule-soft bg-panel p-5">
      <div>
        <h3 className="text-sm font-semibold text-ink">Payload storage</h3>
        <p className="mt-1 text-xs text-dim">
          Keep this organization&rsquo;s indexed source in your own S3-compatible bucket. Only the
          payloads move — metadata and vectors stay in the shared database, so retrieval still ranks
          your private packages against the public catalog in one query.
        </p>
      </div>

      {loading ? (
        <p className="text-xs text-dim">Loading…</p>
      ) : (
        <>
          <p className="text-2xs text-dim">
            {config?.configured ? (
              <>
                Payloads are written to <code className="font-mono">{config.bucket}</code>.
              </>
            ) : (
              'Using the instance default storage.'
            )}
          </p>

          {config?.configured ? (
            <p className="border border-warn/40 bg-warn/10 px-3 py-2 text-2xs text-warn">
              Changing the bucket does not move payloads already written to the old one. Packages
              indexed before the change must be re-indexed to be readable again.
            </p>
          ) : null}

          {!canManage && (
            <p className="text-xs text-dim">
              Only organization owners and admins can change storage settings.
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
            <fieldset disabled={!canManage} className="min-w-0 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                    Bucket
                  </span>
                  <input
                    value={bucket}
                    onChange={(e) => setBucket(e.target.value)}
                    placeholder="acme-llmcontext"
                    className="lc-input py-1.5 font-mono text-xs"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                    Region
                  </span>
                  <input
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="us-east-1 or auto"
                    className="lc-input py-1.5 font-mono text-xs"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
              </div>

              <label className="flex flex-col gap-1">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                  Endpoint <span className="normal-case">(blank for AWS S3)</span>
                </span>
                <input
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  placeholder="https://fly.storage.tigris.dev"
                  className="lc-input py-1.5 font-mono text-xs"
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                    Access key ID
                  </span>
                  <input
                    value={accessKeyId}
                    onChange={(e) => setAccessKeyId(e.target.value)}
                    className="lc-input py-1.5 font-mono text-xs"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                    Secret access key
                  </span>
                  <input
                    type="password"
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder={config?.configured ? 'Stored — type to replace' : ''}
                    className="lc-input py-1.5 font-mono text-xs"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
              </div>

              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={forcePathStyle}
                  onChange={(e) => setForcePathStyle(e.target.checked)}
                  className="h-4 w-4 rounded border-rule text-accent focus:ring-accent"
                />
                <span className="text-xs text-link">
                  Force path-style addressing (required for MinIO)
                </span>
              </label>

              {canManage && (
                <div className="flex items-center justify-between gap-3">
                  <div className="text-2xs text-dim">
                    {config?.configured ? (
                      <button
                        type="button"
                        onClick={() => setConfirmingReset(true)}
                        disabled={saving}
                        className="text-del hover:underline disabled:opacity-50"
                      >
                        Revert to instance storage
                      </button>
                    ) : (
                      'Credentials are encrypted before storage and never returned by a read.'
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {savedAt ? <span className="text-2xs text-good">Saved</span> : null}
                    <Button type="submit" size="sm" disabled={saving || !canSave}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              )}
            </fieldset>
          </form>

          {error ? <p className="text-xs text-del">{error}</p> : null}
        </>
      )}

      {confirmingReset ? (
        <ConfirmDialog
          title="Revert to instance storage?"
          message={
            'New indexes will be written to the instance default backend. Payloads already in ' +
            `${config?.bucket ?? 'your bucket'} stay there and become unreadable until those ` +
            'packages are re-indexed.'
          }
          confirmLabel="Revert"
          tone="danger"
          isConfirming={saving}
          onConfirm={() => void handleReset()}
          onClose={() => setConfirmingReset(false)}
        />
      ) : null}
    </div>
  );
}
